import React from "react";
import { CheckCircle2, Ship, FileCheck, Boxes, Send, Handshake, Package } from "lucide-react";
import { Section, Reveal, Overline, Button } from "../components/primitives";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import { TEXTURES } from "../lib/api";

const FormBlock = ({ heading, sub, type, multiStep = false }) => (
  <Section className="py-20 bg-muted/40 rounded-none">
    <div className="max-w-3xl mx-auto text-center mb-10">
      <Overline>Enquiry</Overline>
      <h2 className="display text-4xl md:text-5xl text-botanical mt-3">{heading}</h2>
      {sub && <p className="text-muted-foreground font-light mt-4">{sub}</p>}
    </div>
    <div className="max-w-4xl mx-auto"><EnquiryForm enquiryType={type} multiStep={multiStep} /></div>
  </Section>
);

const Grid = ({ items, cols = "lg:grid-cols-3" }) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-6`}>
    {items.map((it, i) => (
      <Reveal key={i} delay={(i % 3) * 0.07}>
        <div className="border border-border rounded-md p-8 bg-white h-full hover:border-botanical/40 transition-colors">
          {it.icon && <it.icon className="h-7 w-7 text-turmeric mb-5" strokeWidth={1.3} />}
          {it.n && <div className="font-serif text-4xl text-turmeric mb-4">{it.n}</div>}
          <h3 className="font-serif text-2xl text-botanical">{it.title}</h3>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{it.body}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

// ---------- PARTNER (6-step) ----------
export function Partner() {
  return (
    <div>
      <Seo title="Partner With Us — Distributors & International Buyers" description="Become a Cultiva Botanics partner. We are building international partnerships with importers, distributors, wholesalers, retailers and e-commerce businesses." />
      <PageHero chapter="Partnership" overline="Tell Us About Your Market" title="Tell Us About Your Market" subtitle="We are developing international partnerships with importers, distributors, wholesalers, retailers and e-commerce businesses. Share your market and let's explore the opportunity together." />
      <Section className="py-20"><div className="max-w-4xl mx-auto"><EnquiryForm enquiryType="b2b" multiStep /></div></Section>
    </div>
  );
}

// ---------- DISTRIBUTOR ----------
export function Distributor() {
  const why = [
    { title: "India-Origin Product Portfolio", body: "Botanical and superfood products rooted in India's ingredient ecosystem." },
    { title: "Premium Brand Positioning", body: "Designed for modern wellness consumers who value quality and authenticity." },
    { title: "Growing Portfolio", body: "The product architecture can expand well beyond the initial range." },
    { title: "Global Vision", body: "Built with international market development in mind from day one." },
    { title: "Long-Term Partnerships", body: "Focused on sustainable, mutually beneficial commercial relationships." },
  ];
  return (
    <div>
      <Seo title="Become a Distributor" description="Partner with an India-origin botanical wellness brand. Explore distribution opportunities for Cultiva Botanics' growing superfood portfolio in your market." />
      <PageHero chapter="Distribution" overline="Become a Distributor" title="Become a Cultiva Botanics Distributor" subtitle="Partner with an India-origin botanical wellness brand and explore opportunities to bring our growing product portfolio to your market." />
      <Section className="py-20">
        <div className="mb-12"><Overline>Who We Partner With</Overline><h2 className="display text-4xl md:text-5xl text-botanical mt-3 max-w-2xl">Importers, distributors, retail networks & e-commerce businesses.</h2></div>
        <Grid items={why} />
      </Section>
      <FormBlock heading="Start a Distribution Conversation" sub="Complete the enquiry and our team will follow up to explore your market." type="distributor" multiStep />
    </div>
  );
}

// ---------- WHOLESALE ----------
export function Wholesale() {
  const aud = ["Retailers", "Distributors", "Importers", "Online stores", "Wellness stores", "Supermarkets", "Specialty food retailers"];
  return (
    <div>
      <Seo title="Wholesale & Trade" description="Wholesale botanical superfoods from India — Moringa, Hibiscus, Beetroot, Amla, Turmeric and Makhana. Request wholesale information from Cultiva Botanics." />
      <PageHero chapter="Wholesale" overline="Wholesale & Trade" title="Wholesale & Trade" subtitle="For retailers, distributors, importers, online stores, wellness stores, supermarkets and specialty food retailers looking to stock botanical wellness and superfood products." />
      <Section className="py-16">
        <div className="flex flex-wrap gap-3">{aud.map((a) => <span key={a} className="border border-border rounded-full px-5 py-2 text-sm text-botanical/80">{a}</span>)}</div>
      </Section>
      <FormBlock heading="Request Wholesale Information" sub="Tell us about your business and requirements." type="wholesale" />
    </div>
  );
}

// ---------- EXPORT ENQUIRY ----------
export function ExportEnquiry() {
  return (
    <div>
      <Seo title="Export Enquiry — Indian Botanical Supplier" description="Looking for an Indian botanical supplier? Request export information for Moringa, Hibiscus, Beetroot, Amla, Turmeric and Makhana from Cultiva Botanics." />
      <PageHero chapter="Export" overline="Export Enquiry" title="Looking for an Indian Botanical Supplier?" subtitle="Tell us what you are looking for and our team will review your requirements." />
      <FormBlock heading="Request Export Information" sub="Share your product, quantity and destination details below." type="export" />
    </div>
  );
}

// ---------- INTERNATIONAL BUYERS ----------
export function InternationalBuyers() {
  const steps = [
    { n: "01", title: "Who We Are", body: "An India-origin botanical wellness and superfood brand built for global markets." },
    { n: "02", title: "Our Portfolio", body: "A growing range of botanical powders and plant-based super snacks." },
    { n: "03", title: "Export Orientation", body: "Built from day one with international market development in mind." },
    { n: "04", title: "Documentation", body: "Product documentation available upon qualified business enquiry." },
  ];
  return (
    <div>
      <Seo title="For International Buyers" description="Cultiva Botanics for international buyers — an India-origin botanical superfood brand seeking qualified distribution and import partners across global markets." />
      <PageHero chapter="Global" overline="For International Buyers" title="For International Buyers" subtitle="An India-origin botanical wellness brand, export-oriented and seeking qualified partners across global markets." />
      <Section className="py-20"><Grid items={steps} cols="lg:grid-cols-4" /></Section>
      <FormBlock heading="Start a Business Conversation" sub="Introduce your company and market and our team will be in touch." type="international" />
    </div>
  );
}

// ---------- GLOBAL MARKETS ----------
export function GlobalMarkets() {
  const process = [
    { n: "01", title: "Source", body: "Carefully selected botanical and superfood ingredients.", icon: Package },
    { n: "02", title: "Select", body: "Ingredient and product selection for the range.", icon: Boxes },
    { n: "03", title: "Quality", body: "Quality checks and product documentation.", icon: FileCheck },
    { n: "04", title: "Pack", body: "Products prepared for the relevant market.", icon: Package },
    { n: "05", title: "Prepare", body: "Export documentation and shipment coordination.", icon: Send },
    { n: "06", title: "Partner", body: "Long-term relationship with distributors and buyers.", icon: Handshake },
  ];
  const markets = ["India", "UAE / GCC", "Europe", "United Kingdom", "United States", "Canada", "Australia", "Asia-Pacific", "Africa"];
  return (
    <div>
      <Seo title="Global Markets & Export" description="Indian botanicals, global opportunity. Cultiva Botanics is developing international markets and seeking qualified distribution partners worldwide." />
      <PageHero chapter="Export" overline="Indian Botanicals. Global Opportunity." title="Indian Botanicals. Global Opportunity." subtitle="Rooted in India's botanical heritage, Cultiva Botanics is developing international markets and building qualified partnerships across the world." />
      <Section className="py-20">
        <Overline>From Source to Shipment</Overline>
        <h2 className="display text-4xl md:text-5xl text-botanical mt-3 mb-12">A considered, quality-focused process.</h2>
        <Grid items={process} />
      </Section>
      <Section className="py-16 bg-muted/40 rounded-none">
        <Overline>International Market Development</Overline>
        <h2 className="display text-4xl md:text-5xl text-botanical mt-3">Global Focus Markets</h2>
        <p className="text-muted-foreground font-light mt-4 max-w-2xl">We are seeking qualified distribution partners across these focus markets. We do not claim active distribution unless verified.</p>
        <div className="flex flex-wrap gap-3 mt-8">{markets.map((m) => <span key={m} className="border border-border bg-white rounded-full px-5 py-2 text-sm text-botanical/80">{m}</span>)}</div>
      </Section>
      <Section className="py-20">
        <div className="border border-border rounded-md p-8 md:p-12 bg-white grid md:grid-cols-2 gap-8 items-center">
          <div>
            <Overline>Global Shipping</Overline>
            <h3 className="display text-3xl text-botanical mt-3">Global Shipping & Export Enquiries</h3>
            <p className="text-muted-foreground font-light mt-4 leading-relaxed">International availability, import requirements, customs, duties, taxes and product-specific regulations vary by destination market. Contact our team for market-specific information.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Button to="/export-enquiry">Export Enquiry</Button>
            <Button to="/become-a-distributor" variant="outline">Distributor Enquiry</Button>
            <Button to="/wholesale" variant="outline">Wholesale Enquiry</Button>
          </div>
        </div>
      </Section>
      <FormBlock heading="Bring Cultiva Botanics to Your Market" type="b2b" multiStep />
    </div>
  );
}

// ---------- QUALITY ----------
export function Quality() {
  const docs = ["Product specifications", "Certificate of Analysis", "Laboratory reports", "Ingredient specifications", "Nutritional information", "Packaging specifications", "Shelf life", "Regulatory documentation"];
  return (
    <div>
      <Seo title="Quality & Transparency" description="Quality and transparency at Cultiva Botanics. Product documentation is available upon qualified business enquiry." />
      <PageHero chapter="Quality" overline="Quality & Transparency" title="Quality & Transparency" subtitle="A commitment to consistent product quality and clear, honest documentation for our business partners." />
      <Section className="py-20">
        <div className="grid md:grid-cols-2 gap-3">
          {docs.map((d) => (
            <div key={d} className="flex items-center gap-3 border border-border rounded-md p-5 bg-white">
              <CheckCircle2 className="h-5 w-5 text-moringa shrink-0" /><span className="text-botanical/80">{d}</span>
            </div>
          ))}
        </div>
        <div className="mt-10 border border-border rounded-md p-8 bg-muted/40">
          <p className="font-serif text-2xl text-botanical italic">Product documentation is available upon qualified business enquiry.</p>
          <div className="mt-6 flex flex-wrap gap-4"><Button to="/partner">Business Enquiry</Button><Button to="/export-enquiry" variant="outline">Export Enquiry</Button></div>
        </div>
      </Section>
    </div>
  );
}

// ---------- PRIVATE LABEL ----------
export function PrivateLabel() {
  const cap = ["Bulk supply", "Private label", "Custom packaging", "Custom formulations", "Contract manufacturing"];
  return (
    <div>
      <Seo title="Private Label — Coming Soon" description="Private label and bulk supply opportunities with Cultiva Botanics — coming soon. Register your interest for future private-label partnerships." />
      <PageHero chapter="Private Label" overline="Coming Soon" title="Private Label" subtitle="We are building future-ready private label and bulk supply capabilities. Register your interest and we'll be in touch as these become available." />
      <Section className="py-20">
        <span className="inline-block text-xs uppercase tracking-widest bg-botanical text-ivory px-4 py-2 rounded-full">Coming Soon</span>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
          {cap.map((c) => <div key={c} className="border border-border rounded-md p-6 bg-white text-botanical/80">{c}</div>)}
        </div>
        <div className="mt-10"><Button to="/partner">Register Interest</Button></div>
      </Section>
    </div>
  );
}
